import { useState, useEffect, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  AppointmentSession,
  getSession,
  setSession as saveSession,
  clearSession as removeSession,
  isSessionValid,
} from "../lib/session";

export function useSession(appointmentId?: string) {
  const [session, setSessionState] = useState<AppointmentSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const touchSession = useMutation(api.auth.appointmentSessions.updateAppointmentSessionActivity);

  useEffect(() => {
    const stored = getSession();
    setSessionState(stored);
    setIsLoading(false);
  }, []);

  const isValid = appointmentId
    ? isSessionValid(appointmentId)
    : session !== null;

  const createSession = useCallback(
    (appointmentId: string, expiresAt: number, sessionToken: string) => {
      const newSession: AppointmentSession = {
        appointmentId: String(appointmentId).trim(),
        validatedAt: Date.now(),
        expiresAt,
        sessionToken,
      };
      saveSession(newSession);
      setSessionState(newSession);
    },
    []
  );

  const clearSession = useCallback(() => {
    removeSession();
    setSessionState(null);
  }, []);

  const keepAlive = useCallback(async () => {
    const current = getSession();
    if (!current) return false;
    try {
      const updated = await touchSession({ sessionToken: current.sessionToken });
      // If server says invalid, clear local session
      if (!updated) {
        clearSession();
      }
      return updated;
    } catch {
      return false;
    }
  }, [clearSession, touchSession]);

  return {
    session,
    isValid,
    isLoading,
    createSession,
    clearSession,
    keepAlive,
  };
}
